import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiButton,
  MiCheckbox,
  MiChip,
  MiControlledTextField,
  MiIcon,
  MiIconButton,
  MiInputTextbox,
  MiModal,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  PLAN_CONFIG_STATUS,
} from "@miplatform/constants";
import { handleInputRestrictions, toUpperCase } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiAlertCircleOutline,
  mdiArrowULeftTop,
  mdiLockOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MaterialCard } from "../../../../../components/MaterialCard/MaterialCard";
import { PlaceholderMaterial } from "../../../../../components/PlaceholderMaterial/PlaceholderMaterial";
import { useImportPlanAreaViewModel } from "./useImportPlanAreaViewModel";

const schema = z.object({
  removalReason: z.string().min(1, "Removal Reason is required"),
});

export const ImportPlanArea = ({
  area = {},
  stateManager,
  setShowCategoryFilter,
  planDetails,
  setPlanDetails,
  categories,
  dismissedCategories,
  selectedPlaceholder,
  setSelectedPlaceholder,
  setCheckedAreas,
  showCategoryFilter,
  selectedMaterial,
  setSelectedMaterial,
  handleCategoryClick,
  categoryFilterOptions,
}) => {
  const [openReplaceModal, setOpenReplaceModal] = useState(false);
  const [openDoNotReplaceModal, setOpenDoNotReplaceModal] = useState(false);
  const [openQtyChangeModal, setOpenQtyChangeModal] = useState(false);
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [isQtySmaller, setIsQtySmaller] = useState(false);
  const [prevQty, setPrevQty] = useState(null);

  const {
    formState: { isValid },
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: { removalReason: "" },
    resolver: zodResolver(schema),
  });

  const {
    isLoading,
    materials,
    getMaterials,
    setMaterials,
    handleMarkMaterialAsViewed,
    handleMarkAreaAsReviewed,
    handleAddRemovalReason,
    handleMarkMaterialAsRemoved,
    handleRemoveOptionalMaterial,
    handleReplaceRequiredMaterial,
    handleUndoRemovedMaterial,
    handleRemoveAddedMaterial,
    handleUpdateMaterialQuantity,
    checkArea,
  } = useImportPlanAreaViewModel({
    area,
    stateManager,
    planDetails,
    setPlanDetails,
    setCheckedAreas,
  });

  const allMaterialsViewed = area.materials.every(
    (material) => material.isViewed
  );

  const hasApprovedMaterials = area.materials.every(
    (material) => !material.planConfigStatus
  );

  const hasModifiedMaterials = area.materials.some((material) =>
    [PLAN_CONFIG_STATUS.ADDED, PLAN_CONFIG_STATUS.REMOVED].includes(
      material.planConfigStatus
    )
  );

  const { classes, theme } = useStyles({
    hasModifiedMaterials: hasModifiedMaterials && area.checked,
    hasApprovedMaterials: hasApprovedMaterials && area.checked,
  });

  useEffect(() => {
    if (area.materials.length > 0) {
      getMaterials();
    } else {
      setMaterials([]);
    }
  }, [area.materials]);

  // auto check all materials as viewed if area is checked
  useEffect(() => {
    if (area.checked) {
      area.materials.forEach((material) => {
        if (!material.isViewed) {
          handleMarkMaterialAsViewed(material.id);
        }
      });
    }
  }, [area.checked, area.materials]);

  const sortedMaterials = useMemo(() => {
    return materials.toSorted((a, b) => {
      if (a.referenceId && b.referenceId && a.referenceId === b.referenceId) {
        if (
          a.planConfigStatus === PLAN_CONFIG_STATUS.ADDED &&
          b.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED
        ) {
          return -1;
        }
        if (
          a.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED &&
          b.planConfigStatus === PLAN_CONFIG_STATUS.ADDED
        ) {
          return 1;
        }
      }
      return 0;
    });
  }, [materials]);

  const sortedCategories = useMemo(
    () =>
      categories
        .toSorted((a, b) => {
          if (a.isRequired !== b.isRequired) {
            return b.isRequired - a.isRequired;
          }
          return a.displayOrder - b.displayOrder;
        })
        .filter((cat) => {
          const isDismissed = dismissedCategories.includes(cat.categoryId);
          return isDismissed
            ? !isDismissed
            : !materials.some(
                (material) => cat.categoryId === material.category.categoryId
              );
        }),
    [categories, materials, dismissedCategories]
  );

  // helper to show only the selected category
  const selectCategoryOption = (categoryId) => {
    const selectedCategory = categoryFilterOptions.find(
      (cat) => cat.categoryId === categoryId
    );
    if (selectedCategory) {
      setShowCategoryFilter(true);
      handleCategoryClick(selectedCategory);
    }
  };

  const handleMaterialAction = (material) => {
    setSelectedMaterial(material);

    if (material.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED) {
      handleUndoRemovedMaterial(material.id, material.referenceId);
      return;
    }

    if (material.planConfigStatus === PLAN_CONFIG_STATUS.ADDED) {
      handleRemoveAddedMaterial(material.id);
      selectCategoryOption(material.categoryId);
      return;
    }

    if (material.isRequired) {
      setOpenReplaceModal(true);
    } else {
      handleRemoveOptionalMaterial(material.id);
    }
  };

  const handleReplaceItem = (materialId, categoryId) => {
    handleReplaceRequiredMaterial(materialId);
    selectCategoryOption(categoryId);
  };

  const handleDoNotReplace = () => {
    setOpenReplaceModal(false);
    setOpenDoNotReplaceModal(true);
  };

  const handleYesReplace = () => {
    handleMarkMaterialAsRemoved(selectedMaterial.id);
    setOpenReplaceModal(false);
    selectCategoryOption(selectedMaterial.categoryId);
  };

  const handleBackToFirstModal = () => {
    setOpenDoNotReplaceModal(false);
    setOpenReplaceModal(true);
    reset();
  };

  const handleSubmitReason = () => {
    handleAddRemovalReason(selectedMaterial.id, getValues("removalReason"));
    setOpenDoNotReplaceModal(false);
    reset();
  };

  const handleAddOptionalMaterial = (category) => {
    checkArea();
    setSelectedMaterial({});
    setSelectedPlaceholder(category.categoryId);
    selectCategoryOption(category.categoryId);
  };

  const handleQuantityBlur = (material) => {
    setEditingMaterialId(material.id);
    if (material.quantity === selectedMaterial.quantity) {
      return;
    }
    setIsQtySmaller(material.quantity < selectedMaterial.quantity);
    setOpenQtyChangeModal(true);
  };

  const resetQtyChangeModalState = () => {
    setEditingMaterialId(null);
    setOpenQtyChangeModal(false);
    setIsQtySmaller(false);
    setPrevQty(null);
  };

  const handleCloseQtyChangeModal = () => {
    if (editingMaterialId) {
      handleUpdateMaterialQuantity(editingMaterialId, prevQty);
    }
    resetQtyChangeModalState();
  };

  const getMaterialBgColor = (planConfigStatus) => {
    if (planConfigStatus === PLAN_CONFIG_STATUS.REMOVED) {
      return theme.palette.red.light;
    }
    if (planConfigStatus === PLAN_CONFIG_STATUS.ADDED) {
      return theme.palette.green.light;
    }
    return null;
  };

  return (
    <Accordion className={classes.accordionContainer} expanded disableGutters>
      <AccordionSummary className={classes.accordionSummary}>
        <Box className={classes.areaActionsContainer}>
          <Box className={classes.areaInfoContainer}>
            {area.checked && hasModifiedMaterials && (
              <MiChip
                label="Modified"
                size="small"
                className={classes.modifiedChip}
              />
            )}
            {area.checked && hasApprovedMaterials && (
              <MiChip
                label="Approved"
                size="small"
                className={classes.approvedChip}
              />
            )}
            {!area.checked && (
              <MiIcon
                path={mdiLockOutline}
                size={1}
                color={theme.palette.red.main}
              />
            )}
            <Typography fontWeight={600}>{area.label}</Typography>
          </Box>
          <Box className={classes.areaButtonContainer}>
            {!area.checked && (
              <MiButton
                title="Reviewed"
                variant={BUTTON_VARIANTS.CONTAINED}
                disabled={
                  !allMaterialsViewed || area.checked || showCategoryFilter
                }
                onClick={handleMarkAreaAsReviewed}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box className={classes.materialsContainer}>
          {sortedMaterials.map((material) => {
            return (
              <Box className={classes.materialItemContainer} key={material.id}>
                {!material.removalReason && (
                  <MiIconButton
                    onClick={() => handleMaterialAction(material)}
                    sx={{ display: area.checked ? "none" : "block" }}
                    disabled={showCategoryFilter}
                  >
                    <MiIcon
                      path={
                        material.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED
                          ? mdiArrowULeftTop
                          : mdiTrashCanOutline
                      }
                      color={
                        material.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED
                          ? null
                          : theme.palette.red.main
                      }
                      size={1}
                    />
                  </MiIconButton>
                )}
                {material.removalReason ? (
                  <RemovedReasonCard
                    material={material}
                    disabled={area.checked || showCategoryFilter}
                    handleReplaceItem={() =>
                      handleReplaceItem(material.id, material.categoryId)
                    }
                  />
                ) : (
                  <MaterialCard
                    key={material.id}
                    item={material}
                    isSuppliedByBuilder={material.suppliedByBuilder}
                    showPrice
                    fieldNote={material.fieldNote}
                    customBgColor={getMaterialBgColor(
                      material.planConfigStatus
                    )}
                    isRemoved={
                      material.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED
                    }
                    leftAction={
                      <TextField
                        value={material.quantity}
                        className={classes.textFieldSmall}
                        size="small"
                        disabled={
                          material.planConfigStatus !==
                            PLAN_CONFIG_STATUS.ADDED || area.checked
                        }
                        onChange={(e) =>
                          handleUpdateMaterialQuantity(
                            material.id,
                            e.target.value
                          )
                        }
                        onKeyDown={handleInputRestrictions}
                        onBlur={() => handleQuantityBlur(material)}
                        onFocus={() => setPrevQty(material.quantity)}
                        type="number"
                        autoFocus
                      />
                    }
                  />
                )}
                {material.isRequired && (
                  <Typography className={classes.asterisk}>*</Typography>
                )}
                <MiCheckbox
                  label="Viewed"
                  value={material.isViewed || false}
                  disabled={area.checked || showCategoryFilter}
                  handleChange={() => handleMarkMaterialAsViewed(material.id)}
                />
              </Box>
            );
          })}
          {sortedCategories.map((cat) => {
            return (
              <PlaceholderMaterial
                key={cat.categoryId + area.id}
                category={cat}
                isSelected={selectedPlaceholder === cat.categoryId}
                disabled={isLoading || area.checked || showCategoryFilter}
                preview={area.preview}
                onClick={() => handleAddOptionalMaterial(cat)}
              />
            );
          })}
        </Box>
        <MiModal
          open={openReplaceModal}
          closeModal={() => {
            setOpenReplaceModal(false);
            reset();
          }}
          title="You have removed a required Item"
          description="You have removed an item that is required for this area. Would you like to replace it with an item from the same category?"
          actions={[
            {
              name: "Do Not Replace",
              className: classes.doNotReplaceBtn,
              onClick: handleDoNotReplace,
            },
            {
              name: "Yes, Replace",
              variant: BUTTON_VARIANTS.CONTAINED,
              onClick: handleYesReplace,
            },
          ]}
        />
        <MiModal
          open={openDoNotReplaceModal}
          closeModal={() => {
            setOpenDoNotReplaceModal(false);
            reset();
          }}
          title="You have removed a required Item"
          description="You have removed an item that is required for this area. Please provide a reason for not replacing the required item."
          actions={[
            {
              name: "Back",
              className: classes.modalBackBtn,
              onClick: handleBackToFirstModal,
            },
            {
              name: "Submit Response",
              variant: BUTTON_VARIANTS.CONTAINED,
              onClick: handleSubmitReason,
              disabled: !isValid,
            },
          ]}
        >
          <MiControlledTextField
            formControl={control}
            name="removalReason"
            multiline
            placeholder="Reason for no replacement"
          />
        </MiModal>
        <MiModal
          open={openQtyChangeModal}
          closeModal={handleCloseQtyChangeModal}
          title="Confirm Quantity Change"
          description={
            <span>
              You have selected a{" "}
              <span
                style={{
                  color: isQtySmaller
                    ? theme.palette.red.main
                    : theme.palette.blue.main,
                  fontWeight: theme.fontWeight.semiBold,
                }}
              >
                {isQtySmaller ? "smaller" : "larger"} quantity
              </span>{" "}
              than the deleted Item. Do you want to proceed with this
              replacement?
            </span>
          }
          titleIcon={mdiAlertCircleOutline}
          leftIconColor={
            isQtySmaller ? theme.palette.red.main : theme.palette.blue.main
          }
          actions={[
            {
              name: "Cancel",
              className: classes.modalBackBtn,
              onClick: handleCloseQtyChangeModal,
            },
            {
              name: "Continue",
              variant: BUTTON_VARIANTS.CONTAINED,
              color: isQtySmaller ? BUTTON_COLORS.RED : BUTTON_COLORS.BLUE,
              onClick: resetQtyChangeModalState,
            },
          ]}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const RemovedReasonCard = ({ material, disabled, handleReplaceItem }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.removedCardContainer}>
      <Box>
        <TextField
          value={material.quantity}
          className={classes.textFieldSmall}
          size="small"
          disabled
        />
      </Box>
      <Box className={classes.removedCardInfoContainer}>
        <Tooltip
          title={`${material.category.itemCode} | ${material.category.itemDescription}`}
        >
          <span>
            <MiChip
              label={toUpperCase(material.category.categoryName)}
              size="small"
              className={classes.chip}
            />
          </span>
        </Tooltip>
        <Box className={classes.removedCardReasonContainer}>
          <MiInputTextbox
            value={material.removalReason}
            label="Reason"
            disabled
          />
        </Box>
      </Box>
      <Box>
        <MiButton
          title="Replace Required Item"
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.RED}
          onClick={handleReplaceItem}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(
  (theme, { hasModifiedMaterials, hasApprovedMaterials } = {}) => {
    const getBackgroundColor = () => {
      if (hasModifiedMaterials) {
        return theme.palette.red.light;
      }
      return theme.palette.common.white;
    };

    const getBorderColor = () => {
      if (hasApprovedMaterials) {
        return `2px solid ${theme.palette.orange.main}`;
      }
      if (hasModifiedMaterials) {
        return `2px solid ${theme.palette.red.main}`;
      }
      return `2px solid ${theme.palette.teal.main}`;
    };

    return {
      accordionContainer: {
        backgroundColor: getBackgroundColor(),
        border: getBorderColor(),
        width: "100%",
        margin: 0,
        padding: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderRadius: 4,
        "&::before": {
          content: "none",
        },
        "&.Mui-expanded": {
          margin: 0,
        },
        "& .MuiAccordion-heading": {
          margin: 0,
          alignItems: "center",
          width: "100%",
          flexDirection: "column",
        },
        "& .MuiAccordionSummary-content": {
          margin: 0,
          alignItems: "center",
          width: "100%",
          flexDirection: "column",
        },
        "& .MuiCollapse-root": {
          width: "100%",
        },
      },
      accordionSummary: {
        flexDirection: "column",
        paddingTop: theme.spacing(1),
        "&.Mui-focusVisible": {
          backgroundColor: theme.palette.common.white,
        },
      },
      areaActionsContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        flex: 1,
      },
      areaInfoContainer: {
        display: "flex",
        flex: 1,
        gap: theme.spacing(1),
        alignItems: "center",
      },
      areaButtonContainer: {
        display: "flex",
        alignItems: "center",
      },
      chip: {
        borderRadius: 4,
      },
      materialsContainer: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexDirection: "column",
      },
      materialItemContainer: {
        width: "100%",
        display: "flex",
      },
      asterisk: {
        color: theme.palette.red.main,
      },
      removedCardContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        border: `1px solid ${theme.palette.grey.accent}`,
        width: "100%",
        padding: theme.spacing(2, 1, 2, 1),
        borderRadius: 4,
        boxShadow: `0px 2px 2px ${theme.palette.grey.accent}`,
      },
      removedCardInfoContainer: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "center",
        width: "100%",
      },
      removedCardReasonContainer: {
        width: "100%",
        display: "flex",
      },
      modifiedChip: {
        border: `1px solid ${theme.palette.red.main}`,
        backgroundColor: theme.palette.red.light,
        borderRadius: 4,
        height: 20,
        color: theme.palette.red.main,
      },
      approvedChip: {
        border: `1px solid ${theme.palette.green.main}`,
        backgroundColor: theme.palette.green.light,
        borderRadius: 4,
        height: 20,
        color: theme.palette.green.main,
      },
      textFieldSmall: {
        width: 50,
      },
      doNotReplaceBtn: {
        textDecoration: "underline",
        color: theme.palette.red.main,
      },
      modalBackBtn: {
        textDecoration: "underline",
      },
    };
  }
);
